import React, { ReactNode } from 'react';

import { classnames, margin } from '@lumapps/classnames';
import {
    Alignment,
    ColorPalette,
    ColorVariant,
    FlexBox,
    FlexBoxProps,
    Heading,
    HeadingProps,
    Icon,
    IconProps,
    Orientation,
    Size,
    SkeletonTypography,
    Text,
    TextProps,
    Typography,
} from '@lumapps/lumx/react';

export interface DrawerSectionProps {
    /** section title */
    title: string | React.ReactNode;
    /** element to display before the title  */
    beforeTitle?: React.ReactNode;
    /** element to display after the title */
    afterTitle?: React.ReactNode;
    /** component wrapper props */
    wrapperProps?: Partial<FlexBoxProps>;
    /** additional props for the title wrapper */
    titleWrapperProps?: Partial<FlexBoxProps>;
    /** additional props for the icon wrapper */
    iconWrapperProps?: Partial<FlexBoxProps>;
    /** additional props for the title */
    headingProps?: Partial<HeadingProps>;
    /** helper text */
    helper?: string;
    /** icon to display besides the section text */
    icon?: string;
    /** additional props to pass to the Icon component */
    iconProps?: Partial<IconProps>;
    /** whether the children are loading or not */
    isLoading?: boolean;
    /** additional props to pass into the internal Text component. */
    textProps?: Partial<TextProps>;
    children?: ReactNode;
}

const CLASSNAME = 'lumx-drawer-section';

/**
 * Component used as a section for displaying info inside the Drawer component
 * @param DrawerSectionProps
 * @returns DrawerSection
 */
export const DrawerSection: React.FC<DrawerSectionProps> = ({
    title,
    children,
    beforeTitle,
    afterTitle,
    wrapperProps = {},
    titleWrapperProps = {},
    iconWrapperProps = {},
    headingProps = {},
    helper,
    isLoading = false,
    icon,
    iconProps,
    textProps,
}) => {
    const withHelper = helper ? (
        <Text
            as="span"
            typography={Typography.caption}
            color={ColorPalette.dark}
            colorVariant={ColorVariant.L2}
            {...textProps}
        >
            {helper}
        </Text>
    ) : null;

    return (
        <FlexBox
            orientation={Orientation.vertical}
            gap={Size.regular}
            {...wrapperProps}
            className={classnames(CLASSNAME, margin('vertical', Size.huge), wrapperProps.className)}
        >
            <FlexBox
                orientation={Orientation.horizontal}
                hAlign={Alignment.center}
                gap={Size.regular}
                {...titleWrapperProps}
            >
                {beforeTitle}

                <Heading as="h3" typography={Typography.overline} {...headingProps}>
                    {title}
                </Heading>

                {afterTitle}
            </FlexBox>

            {icon ? (
                <FlexBox
                    orientation={Orientation.horizontal}
                    gap={Size.regular}
                    hAlign={Alignment.top}
                    {...iconWrapperProps}
                >
                    <Icon
                        icon={icon}
                        size={Size.xs}
                        color={ColorPalette.dark}
                        colorVariant={ColorVariant.L2}
                        {...iconProps}
                    />

                    {isLoading ? <SkeletonTypography width="100px" typography={Typography.body2} /> : children}
                </FlexBox>
            ) : null}

            {!icon && isLoading ? <SkeletonTypography width="100px" typography={Typography.body2} /> : null}
            {!icon && !isLoading ? children : null}
            {isLoading && helper ? <SkeletonTypography typography={Typography.caption} /> : withHelper}
        </FlexBox>
    );
};
